import { Box, Divider, Group, Menu, Stack, Text } from "@mantine/core";
import { useNavigate } from "@tanstack/react-router";
import { orderBy } from "lodash-es";
import { useContext } from "react";
import { useTranslation } from "react-i18next";

import SvgIconRaw from "~/components/SvgIconRaw";
import { applyOrgSelection } from "~/lib/orgSelection";
import { SnackbarContext } from "~/utils/Contexts";

import { Org } from "../../utils/Types";
import RoundIconButton from "../Button/RoundIconButton";

const SwitchOrg = ({ orgs }: { orgs: Org[] }) => {
  const { t } = useTranslation("common");
  const navigate = useNavigate();
  const snackbar = useContext(SnackbarContext);

  const currentOrg: Org = orgs.find((o) => o?.isCurrent);

  return (
    <Menu position="bottom-start" offset={8} shadow="md">
      <Menu.Target>
        <Group
          gap={4}
          wrap="nowrap"
          style={{ cursor: "pointer" }}
          data-testid="org-switcher"
        >
          <Text size="xs" fw={600} data-testid="current-org-name">
            {currentOrg?.displayName}
          </Text>
          <Box
            display={(orgs.length || 0) > 1 ? "flex" : "none"}
            data-testid="org-switcher-dropdown"
          >
            <RoundIconButton size={16}>
              <SvgIconRaw
                name="ChevronDown"
                size={24}
                color="var(--mantine-color-text-tertiary)"
              />
            </RoundIconButton>
          </Box>
        </Group>
      </Menu.Target>
      <Menu.Dropdown
        miw={200}
        // MUI Menu capped its paper at the viewport and scrolled; Mantine's
        // dropdown has no max-height, so a long org list (staff accounts)
        // otherwise runs off-screen with no way to reach the tail.
        mah="calc(100vh - 96px)"
        style={{ overflowY: "auto" }}
      >
        <Stack px={16} py={4} gap={0}>
          <Text size="sm" fw={600}>
            {t("nav.pleaseSelect")}
          </Text>
        </Stack>
        <Divider />
        {orderBy(orgs, (org) => org?.idString?.toLowerCase())?.map((i) => {
          const isCurrent = i?.idString === currentOrg?.idString;
          return (
            <Menu.Item
              key={"org_" + i?.idString}
              onClick={async () => {
                if (!i?.idString) return;
                try {
                  await applyOrgSelection({
                    orgIdString: i.idString,
                    orgs,
                    navigate,
                    // UserConfig is org-invariant (three per-user fields off the
                    // Mongo user doc), so a switch never needs to refetch it.
                    shouldRefetchUserConfig: false,
                  });
                } catch {
                  snackbar.open({
                    text: t("nav.switchOrgError"),
                    type: "error",
                  });
                }
              }}
              disabled={isCurrent}
              aria-disabled={isCurrent || undefined}
              data-testid={`org-option-${i?.idString}`}
              leftSection={
                isCurrent ? (
                  <SvgIconRaw name="Check" size={20} data-testid="CheckIcon" />
                ) : (
                  <Box w={20} />
                )
              }
            >
              <Text size="xs" fw={600}>
                {i?.displayName}
              </Text>
            </Menu.Item>
          );
        })}
      </Menu.Dropdown>
    </Menu>
  );
};

export default SwitchOrg;
